import { captureException } from './captureException'
import { trackAuthenticationError } from '../analytics/tracking'
import { getItem } from '../storeManager'

interface EmailLoginErrorContext {
  email: string
  error: any
}

export const trackEnhancedEmailLoginError = ({
  email,
  error,
}: EmailLoginErrorContext) => {
  // Get anonymised email domain for CS debugging
  const emailDomain = email ? email.split('@')[1]?.toLowerCase() : 'unknown'

  // Categorise error types for better debugging
  let errorCategory = 'unknown'
  let isLikelyUserError = false

  if (error?.status === 401) {
    errorCategory = 'invalid_credentials'
    isLikelyUserError = true
  } else if (error?.status >= 500) {
    errorCategory = 'server_error'
  } else if (error?.status === 429) {
    errorCategory = 'rate_limited'
  } else if (error?.message?.toLowerCase().includes('network')) {
    errorCategory = 'network_error'
  } else if (error?.message?.toLowerCase().includes('timeout')) {
    errorCategory = 'timeout'
  }

  // Track for marketing analytics
  trackAuthenticationError({
    errorType: 'email_login_enhanced',
    authMethod: 'email',
    errorMessage: error?.message || 'Email login failed',
    couponCode: getItem('coupon') || '',
    entryPoint: getItem('EntryPoint') || '',
    statusCode: error?.status || null,
    userAgent: navigator.userAgent,
  })

  // Create a proper Error instance for better Sentry grouping
  const sentryError = new Error(`Email login ${errorCategory} failure`)
  sentryError.name = 'EmailLoginError'
  sentryError.cause = error // Preserve original error

  // Enhanced Sentry context for CS debugging
  captureException(sentryError, {
    context: {
      tags: {
        error_category: errorCategory,
        email_domain: emailDomain,
        likely_user_error: isLikelyUserError,
      },
      extra: {
        original_error: error, // Include original error in extra data
        email_length: email?.length || 0,
        has_special_chars: email ? /[!#$%&'*+/=?^_`{|}~-]/.test(email) : false,
        browser_info: {
          language: navigator.language,
          platform: navigator.platform,
          cookieEnabled: navigator.cookieEnabled,
        },
        error_details: {
          status: error?.status,
          statusText: error?.statusText,
          message: error?.message,
          stack: error?.stack,
        },
      },
    },
    fingerprint: [
      'email_login_error',
      errorCategory,
      error?.status?.toString() || 'no_status',
    ],
  })

  return {
    errorCategory,
    emailDomain,
    isLikelyUserError,
  }
}
